import Bugsnag from '@bugsnag/js';
import { HereAddress } from '../data/location/autocomplete';
import { ShapeResult } from '../data/location/shapes';
import {
  getGeoInfo,
  getGeoInfoCacheKey,
  processOpenStreetLsResponse,
} from './geo-info';
import {
  provincesAbbreviationList,
  statesAbbreviationList,
} from '../data/location/states';
import { fetchFromCacheBulk } from './redis';
import { countries } from '../data/location/countries';

const countryMapping = {};
countries.forEach((country) => {
  countryMapping[country.alpha2] = country.alpha2;
  countryMapping[country.alpha3] = country.alpha2;
});
const usStatesHash = statesAbbreviationList.reduce(
  (hash, stateAbb) => ({ ...hash, [stateAbb]: true }),
  {}
);
const caProvincesHash = provincesAbbreviationList.reduce(
  (hash, stateAbb) => ({ ...hash, [stateAbb]: true }),
  {}
);
const allowedStatesHash = { ...usStatesHash, ...caProvincesHash };

const getStatesPolygonData = async (address: HereAddress[]) => {
  if (!address?.length) {
    return {};
  }
  const polygons = {};
  await Promise.all(
    address.map(async ({ countryCode, stateCode }) => {
      const upperCountry = countryCode.toUpperCase();
      const upperState = stateCode.toUpperCase();
      if (allowedStatesHash[upperState]) {
        const p = await import(
          `./statesPolygonData/${countryMapping[upperCountry]}/${upperState}.json`
        ).then((shape) => shape.shape);
        polygons[`${countryMapping[upperCountry]}-${stateCode}`] = p;
      }
    })
  );
  return polygons;
};

const getShapesFromAddresses = async (
  addresses: HereAddress[]
): Promise<ShapeResult> => {
  const cacheKeys = addresses.map((address) =>
    getGeoInfoCacheKey(
      address.city,
      address.stateCode,
      countryMapping[address.countryCode],
      true
    )
  );

  const polygonDataCacheResultsMapping: object = {};
  if (cacheKeys.length) {
    let polygonDataCacheResults = [];
    try {
      polygonDataCacheResults = await fetchFromCacheBulk(cacheKeys);
    } catch (e) {
      Bugsnag.notify(
        new Error(`ERROR: Failed to fetch polygon data from cache - ${e}`)
      );
    }

    cacheKeys.forEach((key, i) => {
      polygonDataCacheResultsMapping[key] = {
        cacheKey: key,
        address: addresses[i],
        result: polygonDataCacheResults[i]
          ? processOpenStreetLsResponse(polygonDataCacheResults[i])
          : null,
      };
    });
    const cacheMisses = Object.values(polygonDataCacheResultsMapping).filter(
      (v) => !v.result
    );

    await Promise.all(
      cacheMisses.map(async (cacheMiss) => {
        const { address } = cacheMiss;
        const polygons = await getGeoInfo(
          address.city,
          address.stateCode,
          address.countryCode,
          true
        );
        polygonDataCacheResultsMapping[cacheMiss.cacheKey] = {
          ...polygonDataCacheResultsMapping[cacheMiss.cacheKey],
          result: polygons,
        };
      })
    );
  }

  const resultMap = {};
  cacheKeys.forEach((key) => {
    resultMap[key] = polygonDataCacheResultsMapping[key].result;
  });
  return resultMap;
};

export { getShapesFromAddresses, getStatesPolygonData };
